import { useState, useEffect } from 'react'
import { useAuth } from '../lib/auth'
import { ArrowLeft, Mail, Phone, Building2, Key, Calendar, Edit3, Save, X, Loader2, Lock } from 'lucide-react'
import { Link } from 'react-router-dom'

interface ResidentData {
  first_name: string
  last_name: string
  email: string
  phone: string | null
  building_name: string | null
  unit_number: string | null
  room_label: string | null
  unifi_pin_code: string | null
  lease_start: string | null
  lease_end: string | null
  status: string
}

export default function ResidentProfile() {
  const { token } = useAuth()
  const [profile, setProfile] = useState<ResidentData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [emailForm, setEmailForm] = useState({ new_email: '', current_password: '' })
  const [savingEmail, setSavingEmail] = useState(false)
  const [emailMessage, setEmailMessage] = useState<{ text: string; isError: boolean } | null>(null)

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/auth/resident/profile', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to load profile')
      }

      const data = await response.json()
      // API returns { user: {...}, tenant: {...} } — map to our flat structure
      const tenant = data.tenant || data
      const user = data.user || {}
      setProfile({
        first_name: tenant.first_name || user.first_name || '',
        last_name: tenant.last_name || user.last_name || '',
        email: user.email || tenant.email || '',
        phone: tenant.phone || null,
        building_name: tenant.building_name || null,
        unit_number: tenant.unit_number || null,
        room_label: tenant.room_label || null,
        unifi_pin_code: tenant.pin_code || tenant.unifi_pin_code || null,
        lease_start: tenant.lease_start || null,
        lease_end: tenant.lease_end || null,
        status: tenant.status || 'active',
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  async function handleChangeEmail(e: React.FormEvent) {
    e.preventDefault()
    setEmailMessage(null)
    const newEmail = emailForm.new_email.trim().toLowerCase()
    if (!newEmail) {
      setEmailMessage({ text: 'Please enter a new email address', isError: true })
      return
    }
    if (!emailForm.current_password) {
      setEmailMessage({ text: 'Please enter your current password', isError: true })
      return
    }

    setSavingEmail(true)
    try {
      const res = await fetch('/api/auth/resident/change-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          new_email: newEmail,
          current_password: emailForm.current_password,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setEmailMessage({ text: data.detail || 'Failed to update email', isError: true })
        return
      }

      setEmailMessage({ text: 'Email updated. Refreshing…', isError: false })
      // Reload so the auth context/profile everywhere picks up the new email.
      setTimeout(() => window.location.reload(), 800)
    } catch {
      setEmailMessage({ text: 'Network error. Please try again.', isError: true })
    } finally {
      setSavingEmail(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    )
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">{error || 'Failed to load profile'}</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-400 hover:text-gray-600">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center">
              <span className="text-white text-3xl font-bold">
                {profile.first_name[0]}{profile.last_name[0]}
              </span>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {profile.first_name} {profile.last_name}
              </h1>
              <p className="text-gray-600">Resident Portal</p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <div className="flex items-center gap-3">
                  <p className="font-medium text-gray-900">{profile.email}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setEmailMessage(null)
                      setEmailForm({ new_email: profile.email, current_password: '' })
                      setShowEmailModal(true)
                    }}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-md border border-gray-200 hover:bg-gray-50 text-gray-700"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit
                  </button>
                </div>
              </div>
            </div>
            {profile.phone && (
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium text-gray-900">{profile.phone}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Location Information */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Location</h2>
          <div className="space-y-4">
            {profile.building_name && (
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Building</p>
                  <p className="font-medium text-gray-900">{profile.building_name}</p>
                </div>
              </div>
            )}
            {profile.unit_number && (
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Unit</p>
                  <p className="font-medium text-gray-900">
                    Unit {profile.unit_number}
                    {profile.room_label && ` - ${profile.room_label}`}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Access Information */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Access Information</h2>
          <div className="space-y-4">
            {profile.unifi_pin_code && (
              <div className="flex items-center gap-3">
                <Key className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Door PIN Code</p>
                  <p className="font-medium text-gray-900 text-2xl tracking-wider">
                    {profile.unifi_pin_code}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Lease Information */}
        {(profile.lease_start || profile.lease_end) && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Lease Information</h2>
            <div className="space-y-4">
              {profile.lease_start && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Lease Start</p>
                    <p className="font-medium text-gray-900">
                      {new Date(profile.lease_start).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
              {profile.lease_end && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Lease End</p>
                    <p className="font-medium text-gray-900">
                      {new Date(profile.lease_end).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 bg-primary-50 border-b border-primary-100">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-primary-900">Update Email</h3>
              </div>
              <button
                onClick={() => setShowEmailModal(false)}
                className="p-1 hover:bg-primary-100 rounded-lg"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-primary-600" />
              </button>
            </div>

            <form onSubmit={handleChangeEmail} className="p-6 space-y-4">
              {emailMessage && (
                <div
                  className={`p-3 rounded-lg text-sm border ${
                    emailMessage.isError
                      ? 'bg-red-50 text-red-700 border-red-200'
                      : 'bg-green-50 text-green-700 border-green-200'
                  }`}
                >
                  {emailMessage.text}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Email</label>
                <input
                  type="email"
                  value={emailForm.new_email}
                  onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="password"
                    value={emailForm.current_password}
                    onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter your current password"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  For security, we require your current password to change your email.
                </p>
              </div>

              <div className="pt-2 flex items-center gap-2">
                <button
                  type="submit"
                  disabled={savingEmail}
                  className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50"
                >
                  {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {savingEmail ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEmailModal(false)}
                  disabled={savingEmail}
                  className="px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
